import { useState, useEffect } from 'react';
import {
    UsersIcon,
    ClockIcon,
    ArrowPathIcon,
    EyeIcon,
    XMarkIcon,
    ChartBarIcon,
    ComputerDesktopIcon,
    ArrowTrendingUpIcon
} from '@heroicons/react/24/outline';
import { audit } from '../services/api';
import Modal from '../components/Modal';
import { useToast } from '../context/ToastContext';
import { format, formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

export default function UserActivityAdmin() {
    const toast = useToast();

    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [recentLogins, setRecentLogins] = useState([]);
    const [recentActions, setRecentActions] = useState([]);
    const [activeTab, setActiveTab] = useState('logins'); // 'logins' or 'actions'

    // Modal for action details
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedAction, setSelectedAction] = useState(null);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const data = await audit.getUserActivity(100);
            setStats(data.stats);
            setRecentLogins(data.recentLogins || []);
            setRecentActions(data.recentActions || []);
        } catch (error) {
            console.error(error);
            toast.error('Error al cargar actividad de usuarios');
        } finally {
            setLoading(false);
        }
    };

    const handleViewAction = (action) => {
        setSelectedAction(action);
        setShowDetailModal(true);
    };

    const getActionLabel = (accion) => {
        const labels = {
            'crear': { text: 'Crear', color: 'green' },
            'modificar': { text: 'Modificar', color: 'yellow' },
            'eliminar': { text: 'Eliminar', color: 'red' }
        };
        return labels[accion] || { text: accion, color: 'gray' };
    };

    const getTableLabel = (tabla) => {
        const labels = {
            'dias_conductor_motivo': 'Calendario',
            'conductores': 'Conductores',
            'turnos': 'Turnos',
            'motivos': 'Motivos',
            'avisos': 'Avisos',
            'users': 'Usuarios'
        };
        return labels[tabla] || tabla;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2 sm:gap-3">
                        <ChartBarIcon className="w-6 h-6 sm:w-8 sm:h-8 text-orange-500" />
                        Actividad de Usuarios
                    </h1>
                    <p className="text-gray-400 mt-1 text-sm sm:text-base">
                        Monitoriza conexiones y movimientos en el sistema
                    </p>
                </div>
                <button
                    onClick={loadData}
                    className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-3 bg-white/10 text-white font-medium rounded-xl hover:bg-white/20 transition-all"
                >
                    <ArrowPathIcon className="w-5 h-5" />
                    Actualizar
                </button>
            </div>

            {/* Stats Cards */}
            {stats && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                    <div className="bg-[#1a1a3e]/50 border border-white/10 rounded-xl p-3 sm:p-4 flex items-center gap-3 sm:gap-4">
                        <div className="p-3 bg-orange-500/20 rounded-lg shrink-0">
                            <UsersIcon className="w-5 h-5 sm:w-6 sm:h-6 text-orange-500" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-gray-400 text-xs uppercase tracking-wider truncate">Usuarios Totales</p>
                            <p className="text-xl sm:text-2xl font-bold text-white">{stats.totalUsers}</p>
                        </div>
                    </div>
                    <div className="bg-[#1a1a3e]/50 border border-white/10 rounded-xl p-3 sm:p-4 flex items-center gap-3 sm:gap-4">
                        <div className="p-3 bg-green-500/20 rounded-lg shrink-0">
                            <ClockIcon className="w-5 h-5 sm:w-6 sm:h-6 text-green-400" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-gray-400 text-xs uppercase tracking-wider truncate">Usuarios Activos Hoy</p>
                            <p className="text-xl sm:text-2xl font-bold text-white">{stats.loginsToday}</p>
                        </div>
                    </div>
                    <div className="bg-[#1a1a3e]/50 border border-white/10 rounded-xl p-3 sm:p-4 flex items-center gap-3 sm:gap-4">
                        <div className="p-3 bg-purple-500/20 rounded-lg shrink-0">
                            <ArrowTrendingUpIcon className="w-5 h-5 sm:w-6 sm:h-6 text-purple-400" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-gray-400 text-xs uppercase tracking-wider truncate">Usuarios Activos (7d)</p>
                            <p className="text-xl sm:text-2xl font-bold text-white">{stats.logins7Days}</p>
                        </div>
                    </div>
                    <div className="bg-[#1a1a3e]/50 border border-white/10 rounded-xl p-3 sm:p-4 flex items-center gap-3 sm:gap-4">
                        <div className="p-3 bg-amber-500/20 rounded-lg shrink-0">
                            <ComputerDesktopIcon className="w-5 h-5 sm:w-6 sm:h-6 text-amber-500" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-gray-400 text-xs uppercase tracking-wider truncate">Acciones (7d)</p>
                            <p className="text-xl sm:text-2xl font-bold text-white">{stats.actions7Days}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Tabs */}
            <div className="flex gap-2 overflow-x-auto pb-2">
                <button
                    onClick={() => setActiveTab('logins')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${activeTab === 'logins'
                        ? 'bg-orange-500/20 text-orange-500 border border-orange-500/30'
                        : 'bg-white/5 text-gray-400 hover:bg-white/10 border border-transparent'
                        }`}
                >
                    <ClockIcon className="w-4 h-4" />
                    Conexiones Recientes
                    <span className="ml-1 px-2 py-0.5 bg-orange-500/20 rounded-full text-xs">
                        {recentLogins.length}
                    </span>
                </button>
                <button
                    onClick={() => setActiveTab('actions')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${activeTab === 'actions'
                        ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                        : 'bg-white/5 text-gray-400 hover:bg-white/10 border border-transparent'
                        }`}
                >
                    <ChartBarIcon className="w-4 h-4" />
                    Movimientos
                    <span className="ml-1 px-2 py-0.5 bg-purple-500/20 rounded-full text-xs">
                        {recentActions.length}
                    </span>
                </button>
            </div>

            {/* Logins Tab */}
            {activeTab === 'logins' && (
                <div className="bg-[#1a1a3e]/50 border border-white/10 rounded-xl overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-white/10">
                                    <th className="text-left text-gray-400 text-sm font-medium p-3 sm:p-4">Usuario</th>
                                    <th className="text-left text-gray-400 text-sm font-medium p-3 sm:p-4 hidden sm:table-cell">Email</th>
                                    <th className="text-left text-gray-400 text-sm font-medium p-3 sm:p-4">Última Conexión</th>
                                    <th className="text-left text-gray-400 text-sm font-medium p-3 sm:p-4 hidden md:table-cell">Hace</th>
                                </tr>
                            </thead>
                            <tbody>
                                {recentLogins.length === 0 ? (
                                    <tr>
                                        <td colSpan="4" className="text-center text-gray-500 py-12">
                                            No hay conexiones registradas
                                        </td>
                                    </tr>
                                ) : (
                                    recentLogins.map(login => (
                                        <tr key={login.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                                            <td className="p-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-purple-600 flex items-center justify-center text-white font-medium flex-shrink-0">
                                                        {login.nombre?.charAt(0).toUpperCase() || '?'}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <p className="font-medium text-white truncate">{login.nombre}</p>
                                                        <p className="text-xs text-gray-400 sm:hidden truncate">{login.email}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="p-4 text-gray-400 text-sm hidden sm:table-cell">
                                                {login.email}
                                            </td>
                                            <td className="p-4">
                                                <p className="text-white text-sm">
                                                    {format(new Date(login.last_login), 'dd MMM yyyy', { locale: es })}
                                                </p>
                                                <p className="text-gray-500 text-xs">
                                                    {format(new Date(login.last_login), 'HH:mm', { locale: es })}
                                                </p>
                                            </td>
                                            <td className="p-4 hidden md:table-cell">
                                                <span className={`text-sm ${login.days_ago === 0 ? 'text-green-400' : login.days_ago <= 7 ? 'text-orange-500' : 'text-gray-400'}`}>
                                                    {login.days_ago === 0 ? 'Hoy' : login.days_ago === 1 ? 'Ayer' : `Hace ${login.days_ago} días`}
                                                </span>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Actions Tab */}
            {activeTab === 'actions' && (
                <div className="bg-[#1a1a3e]/50 border border-white/10 rounded-xl overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-white/10">
                                    <th className="text-left text-gray-400 text-sm font-medium p-3 sm:p-4">Fecha</th>
                                    <th className="text-left text-gray-400 text-sm font-medium p-3 sm:p-4 hidden sm:table-cell">Usuario</th>
                                    <th className="text-left text-gray-400 text-sm font-medium p-3 sm:p-4">Acción</th>
                                    <th className="text-left text-gray-400 text-sm font-medium p-3 sm:p-4 hidden md:table-cell">Tabla</th>
                                    <th className="text-left text-gray-400 text-sm font-medium p-3 sm:p-4 hidden lg:table-cell">IP</th>
                                    <th className="text-right text-gray-400 text-sm font-medium p-3 sm:p-4">Detalle</th>
                                </tr>
                            </thead>
                            <tbody>
                                {recentActions.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="text-center text-gray-500 py-12">
                                            No hay movimientos registrados
                                        </td>
                                    </tr>
                                ) : (
                                    recentActions.map(action => {
                                        const actionLabel = getActionLabel(action.accion);
                                        return (
                                            <tr key={action.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                                                <td className="p-4">
                                                    <p className="text-white text-sm">
                                                        {format(new Date(action.created_at), 'dd MMM', { locale: es })}
                                                    </p>
                                                    <p className="text-gray-500 text-xs">
                                                        {format(new Date(action.created_at), 'HH:mm', { locale: es })}
                                                    </p>
                                                </td>
                                                <td className="p-4 text-gray-300 text-sm hidden sm:table-cell">
                                                    {action.usuario_nombre}
                                                </td>
                                                <td className="p-4">
                                                    <span className={`px-2 py-1 rounded text-xs font-medium border
                                                        ${actionLabel.color === 'green' ? 'bg-green-500/10 text-green-400 border-green-500/20' : ''}
                                                        ${actionLabel.color === 'yellow' ? 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' : ''}
                                                        ${actionLabel.color === 'red' ? 'bg-red-500/10 text-red-400 border-red-500/20' : ''}
                                                        ${actionLabel.color === 'gray' ? 'bg-gray-500/10 text-gray-400 border-gray-500/20' : ''}
                                                    `}>
                                                        {actionLabel.text}
                                                    </span>
                                                </td>
                                                <td className="p-4 text-gray-400 text-sm hidden md:table-cell">
                                                    {getTableLabel(action.tabla)}
                                                </td>
                                                <td className="p-4 text-gray-500 text-xs font-mono hidden lg:table-cell">
                                                    {action.ip_address || '-'}
                                                </td>
                                                <td className="p-4 text-right">
                                                    <button
                                                        onClick={() => handleViewAction(action)}
                                                        className="p-2 text-gray-400 hover:text-cyan-400 hover:bg-cyan-500/10 rounded-lg transition-colors"
                                                        title="Ver detalle"
                                                    >
                                                        <EyeIcon className="w-5 h-5" />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Action Detail Modal */}
            <Modal
                isOpen={showDetailModal}
                onClose={() => {
                    setShowDetailModal(false);
                    setSelectedAction(null);
                }}
                title="Detalle del Movimiento"
                size="lg"
            >
                {selectedAction && (
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-gray-400 text-xs uppercase mb-1">Acción</p>
                                <span className={`px-2 py-1 rounded text-xs font-medium border
                                    ${selectedAction.accion === 'crear' ? 'bg-green-500/10 text-green-400 border-green-500/20' : ''}
                                    ${selectedAction.accion === 'modificar' ? 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' : ''}
                                    ${selectedAction.accion === 'eliminar' ? 'bg-red-500/10 text-red-400 border-red-500/20' : ''}
                                `}>
                                    {getActionLabel(selectedAction.accion).text}
                                </span>
                            </div>
                            <div>
                                <p className="text-gray-400 text-xs uppercase mb-1">Tabla</p>
                                <p className="text-white">{getTableLabel(selectedAction.tabla)}</p>
                            </div>
                            <div>
                                <p className="text-gray-400 text-xs uppercase mb-1">Usuario</p>
                                <p className="text-white">{selectedAction.usuario_nombre}</p>
                            </div>
                            <div>
                                <p className="text-gray-400 text-xs uppercase mb-1">IP</p>
                                <p className="text-white font-mono text-sm">{selectedAction.ip_address || 'No registrada'}</p>
                            </div>
                            <div className="col-span-2">
                                <p className="text-gray-400 text-xs uppercase mb-1">Fecha</p>
                                <p className="text-white">
                                    {format(new Date(selectedAction.created_at), "dd 'de' MMMM yyyy 'a las' HH:mm:ss", { locale: es })}
                                </p>
                            </div>
                        </div>

                        {selectedAction.datos_anteriores && (
                            <div>
                                <p className="text-gray-400 text-xs uppercase mb-2">Datos Anteriores</p>
                                <pre className="bg-black/40 border border-white/10 rounded-lg p-3 text-xs text-gray-300 overflow-x-auto max-h-40">
                                    {JSON.stringify(JSON.parse(selectedAction.datos_anteriores), null, 2)}
                                </pre>
                            </div>
                        )}

                        {selectedAction.datos_nuevos && (
                            <div>
                                <p className="text-gray-400 text-xs uppercase mb-2">Datos Nuevos</p>
                                <pre className="bg-black/40 border border-white/10 rounded-lg p-3 text-xs text-gray-300 overflow-x-auto max-h-40">
                                    {JSON.stringify(JSON.parse(selectedAction.datos_nuevos), null, 2)}
                                </pre>
                            </div>
                        )}
                    </div>
                )}
            </Modal>
        </div>
    );
}
